import type { Verse } from '@quranjs/api';
import { groupBy } from 'lodash';
import { useEffect } from 'react';
import { useNavigate, useParams, useRouter } from '@tanstack/react-router';
import { usePageReaderContext } from '@/features/reader/hooks/use-page-reader-context';
import { useLayoutMode } from '@/features/reader/hooks/use-layout-mode';
import { usePageSwipe } from '@/features/reader/hooks/use-page-swipe';
import { READER_PAGES_COUNT } from '@/shared/constants/constants';
import { cn } from '@/shared/lib/utils';

const CHAPTERS_WITH_NO_BASMALAH = ['1', '9'];
const UNICODE_SURAH = '\uE000';
const BASMALAH_UNICODE = '\ufdfd';

// Helper function for chapter name unicode
function getChapterNameUnicode(chapterId: string) {
  return `${chapterId.padStart(3, '0')}${UNICODE_SURAH}`;
}

function Basmalah() {
  return (
    <p dir="rtl" className="flex justify-center">
      <span>{BASMALAH_UNICODE}</span>
    </p>
  );
}

function ChapterLines({ verses }: { verses: Verse[] }) {
  const linesToWordsMap = groupBy(
    verses.flatMap((v) => v.words),
    (word) => word?.lineNumber as number,
  );

  return Object.keys(linesToWordsMap).map((lineNumber) => {
    const words = linesToWordsMap[lineNumber];

    return (
      <p
        dir="rtl"
        key={`${lineNumber}-${words.length}`}
        className="flex justify-center"
      >
        {words.map((word) => {
          return (
            <span key={`${word?.id}-${word?.lineNumber}`}>{word?.codeV1}</span>
          );
        })}
      </p>
    );
  });
}

function ChapterContent({
  chapterId,
  isSpread,
}: {
  chapterId: string;
  isSpread: boolean;
}) {
  const { versesByChapter } = usePageReaderContext();
  const chapterVerses = versesByChapter[chapterId];
  const hasFirstVerseOfChapter = chapterVerses.some(
    (verse) => verse.verseNumber === 1,
  );
  const displayBasmalah = !CHAPTERS_WITH_NO_BASMALAH.includes(chapterId);
  const chapterNameUnicode = getChapterNameUnicode(chapterId);

  const fontSize = isSpread
    ? 'text-[clamp(0.4rem,min(2.5vw,2.8vh),1.5rem)]'
    : 'text-[clamp(1rem,calc(100vw*0.05),2rem)]';

  return (
    <div
      key={`${chapterId}-${chapterVerses.length}`}
      className={cn('grid', isSpread ? 'gap-0.5' : 'gap-2', fontSize)}
    >
      {hasFirstVerseOfChapter ? (
        <div className={isSpread ? 'space-y-1' : 'space-y-4'}>
          <span
            dir="rtl"
            className={cn(
              'font-[surah-names] block text-center rounded-lg bg-secondary',
              isSpread ? 'py-1' : 'py-2',
            )}
          >
            {chapterNameUnicode}
          </span>
          {displayBasmalah && <Basmalah />}
        </div>
      ) : null}
      <ChapterLines verses={chapterVerses} />
    </div>
  );
}

interface FontInfo {
  name: string;
  url: string;
}

function usePrefetchAdjacentFonts(pageNumber: number, isSpread: boolean) {
  // In spread mode, preload the current pair + adjacent spread (±2)
  // In single mode, preload ±1
  const offset = isSpread ? 2 : 1;
  const prevPage = pageNumber - offset;
  const nextPage = pageNumber + offset;

  // Also preload the spread partner in spread mode
  const spreadPartner = isSpread ? pageNumber + 1 : pageNumber;

  // Load fonts for adjacent pages
  useEffect(() => {
    const loadFonts = async () => {
      try {
        const rangeEnd = isSpread
          ? Math.max(nextPage, spreadPartner)
          : nextPage;
        const fonts = getFontsToAdd(prevPage, rangeEnd).filter(
          (f) => !document.fonts.check(`16px "${f.name}"`),
        );
        if (fonts.length === 0) return;
        const fontPromises = fonts.map((font) =>
          new FontFace(font.name, `url(${font.url})`).load(),
        );

        await Promise.all(fontPromises).then((loadedFonts) => {
          for (const font of loadedFonts) {
            document.fonts.add(font);
          }
        });
      } catch (error) {
        console.error('Error loading fonts:', error);
      }
    };

    if (prevPage > 0 || nextPage <= READER_PAGES_COUNT) {
      void loadFonts();
    }
  }, [prevPage, nextPage, isSpread, spreadPartner]);
}

function getFontsToAdd(prevPage: number, nextPage: number): FontInfo[] {
  const fonts: FontInfo[] = [];
  const BASE_URL = '/fonts/hafs/v1/woff2';
  for (
    let i = Math.max(1, prevPage);
    i <= Math.min(nextPage, READER_PAGES_COUNT);
    i++
  ) {
    fonts.push({ name: `page${i}`, url: `${BASE_URL}/p${i}.woff2` });
  }
  return fonts;
}

function usePrefetchAdjacentRoutes(pageNumber: number, isSpread: boolean) {
  const router = useRouter();
  const offset = isSpread ? 2 : 1;
  const prevPage = pageNumber - offset;
  const nextPage = pageNumber + offset;

  useEffect(() => {
    const pages = [prevPage, nextPage].filter(
      (p) => p >= 1 && p <= READER_PAGES_COUNT,
    );
    for (const page of pages) {
      void router.preloadRoute({
        to: '/page/$id',
        params: { id: String(page) },
      });
    }
  }, [prevPage, nextPage, router]);
}

export function PageReaderLines() {
  const navigate = useNavigate();
  const params = useParams({ from: '/page/$id' });
  const pageNumber = Number.parseInt(params.id);
  const { layoutMode } = useLayoutMode();
  const isSpread = layoutMode === 'spread';

  usePrefetchAdjacentFonts(pageNumber, isSpread);
  usePrefetchAdjacentRoutes(pageNumber, isSpread);

  const { handlers: swipeableHandlers } = usePageSwipe({
    currentPageNumber: pageNumber,
    step: isSpread ? 2 : 1,
    onSwiped: (id, direction) => {
      void navigate({
        to: '/page/$id',
        params: { id: String(id) },
        viewTransition: { types: [`slide-${direction}`] },
      });
    },
  });

  const { versesByChapter } = usePageReaderContext();

  const chapterIds = Object.keys(versesByChapter);
  const pageHasMultipleChapters = chapterIds.length > 1;

  return (
    <div
      {...swipeableHandlers}
      className={cn(pageHasMultipleChapters && 'flex flex-col gap-8')}
    >
      {chapterIds.map((chapterId) => {
        return (
          <ChapterContent
            key={chapterId}
            chapterId={chapterId}
            isSpread={isSpread}
          />
        );
      })}
    </div>
  );
}
